import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  getDocs, 
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { notificationModel } from '../data/notificationModel';

/**
 * Database helpers for Video Call System
 */

// ==================== Video Call Schedules ====================

/**
 * Create or update video call schedule
 */
export const saveVideoCallSchedule = async (storeId, scheduleData) => {
  try {
    const scheduleRef = doc(db, 'videoCallSchedules', storeId);
    await setDoc(scheduleRef, {
      ...scheduleData,
      updatedAt: serverTimestamp()
    }, { merge: true });
    return { success: true };
  } catch (error) {
    console.error('Error saving video call schedule:', error);
    throw error;
  }
};

/**
 * Get video call schedule
 */
export const getVideoCallSchedule = async (storeId) => {
  try {
    const scheduleRef = doc(db, 'videoCallSchedules', storeId);
    const scheduleSnap = await getDoc(scheduleRef);
    
    if (scheduleSnap.exists()) {
      return { id: scheduleSnap.id, ...scheduleSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting video call schedule:', error);
    throw error;
  }
};

// ==================== Video Call Requests ====================

/**
 * Create video call request
 */
export const createVideoCallRequest = async (requestData) => {
  try {
    const requestsRef = collection(db, 'videoCallRequests');
    const request = {
      ...requestData,
      status: 'pending',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(requestsRef, request);
    const requestId = docRef.id;
    
    // Create notification for store owner
    try {
      console.log('📤 Creating notification for store owner:', {
        sellerId: requestData.sellerId,
        storeId: requestData.storeId,
        buyerName: requestData.buyerName,
        productName: requestData.productName,
        note: 'Notification will be sent to userId = sellerId (store.ownerId)'
      });
      
      // Debug: Verify sellerId is valid
      if (!requestData.sellerId) {
        console.error('❌ ERROR: sellerId is missing! Cannot create notification.');
        throw new Error('sellerId is required for notification');
      }
      
      const notificationsRef = collection(db, notificationModel.collection);
      const notification = notificationModel.createNotification(
        requestData.sellerId, // Store owner
        notificationModel.types.VIDEO_CALL_REQUEST,
        '📹 Video Call Request ใหม่',
        `${requestData.buyerName || 'ผู้ซื้อ'} ขอ Video Call สำหรับสินค้า "${requestData.productName || 'N/A'}"`,
        {
          requestId: requestId,
          storeId: requestData.storeId,
          productId: requestData.productId,
          buyerId: requestData.buyerId,
          scheduledTime: requestData.scheduledTime
        }
      );
      
      // Ensure read field is explicitly set to false
      const notificationData = {
        ...notification,
        read: false, // Explicitly set to false
        readAt: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      console.log('📤 Notification data before saving:', {
        userId: notificationData.userId,
        type: notificationData.type,
        read: notificationData.read,
        readType: typeof notificationData.read
      });
      
      const notifDoc = await addDoc(notificationsRef, notificationData);
      
      console.log('✅ Notification created successfully:', {
        notificationId: notifDoc.id,
        userId: requestData.sellerId,
        type: notificationModel.types.VIDEO_CALL_REQUEST
      });
    } catch (notifError) {
      console.error('❌ Error creating notification:', notifError);
      console.error('Error details:', {
        sellerId: requestData.sellerId,
        errorCode: notifError.code,
        errorMessage: notifError.message
      });
      // Don't throw - notification is not critical
    }
    
    return { id: requestId, ...request };
  } catch (error) {
    console.error('Error creating video call request:', error);
    throw error;
  }
};

/**
 * Update video call request status
 */
export const updateVideoCallRequest = async (requestId, updates) => {
  try {
    const requestRef = doc(db, 'videoCallRequests', requestId);
    await updateDoc(requestRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating video call request:', error);
    throw error;
  }
};

/**
 * Get video call requests for store
 */
export const getVideoCallRequests = async (storeId, filters = {}) => {
  try {
    const requestsRef = collection(db, 'videoCallRequests');
    let q = query(requestsRef, where('storeId', '==', storeId));
    
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting video call requests:', error);
    throw error;
  }
};

// ==================== Video Calls ====================

/**
 * Create video call record
 */
export const createVideoCall = async (callData) => {
  try {
    // Validate required fields with detailed error messages
    if (!callData || typeof callData !== 'object') {
      throw new Error('callData must be an object');
    }
    
    if (!callData.callerId || typeof callData.callerId !== 'string' || callData.callerId.trim() === '') {
      console.error('❌ Invalid callerId:', callData.callerId);
      throw new Error('callerId is required and must be a non-empty string');
    }
    
    if (!callData.receiverId || typeof callData.receiverId !== 'string' || callData.receiverId.trim() === '') {
      console.error('❌ Invalid receiverId:', callData.receiverId, 'Type:', typeof callData.receiverId);
      throw new Error('receiverId is required and must be a non-empty string');
    }
    
    const callsRef = collection(db, 'videoCalls');
    
    // Remove undefined values to prevent Firestore errors
    // Keep null values for optional fields, but remove undefined
    const cleanedCallData = {};
    Object.keys(callData).forEach(key => {
      if (callData[key] !== undefined) {
        cleanedCallData[key] = callData[key];
      }
    });
    
    // Final validation: receiverId must be a valid string
    if (!cleanedCallData.receiverId || typeof cleanedCallData.receiverId !== 'string') {
      throw new Error('receiverId validation failed: must be a non-empty string');
    }
    
    const call = {
      ...cleanedCallData,
      status: 'active',
      startedAt: cleanedCallData.startedAt || serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    console.log('✅ Creating video call with data:', {
      callerId: call.callerId,
      receiverId: call.receiverId,
      chatId: call.chatId,
      productId: call.productId,
      storeId: call.storeId
    });
    
    const docRef = await addDoc(callsRef, call);
    const callId = docRef.id;
    console.log('✅ Video call created successfully:', callId);
    
    // Send notification to receiver about incoming call
    try {
      // Get caller info for notification
      const callerRef = doc(db, 'users', call.callerId);
      const callerSnap = await getDoc(callerRef);
      const callerName = callerSnap.exists() 
        ? (callerSnap.data().displayName || callerSnap.data().name || 'ผู้ใช้')
        : 'ผู้ใช้';
      
      // Get product/store info if available
      let productName = null;
      let storeName = null;
      
      if (call.productId) {
        const productRef = doc(db, 'products', call.productId);
        const productSnap = await getDoc(productRef);
        if (productSnap.exists()) {
          productName = productSnap.data().name;
        }
      }
      
      if (call.storeId) {
        const storeRef = doc(db, 'stores', call.storeId);
        const storeSnap = await getDoc(storeRef);
        if (storeSnap.exists()) {
          storeName = storeSnap.data().name;
        }
      }
      
      // Create notification message
      let notificationMessage = `${callerName} กำลังโทร Video Call`;
      if (productName) {
        notificationMessage += ` สำหรับสินค้า "${productName}"`;
      } else if (storeName) {
        notificationMessage += ` จากร้าน "${storeName}"`;
      }
      notificationMessage += ' คลิกเพื่อเข้าร่วม';
      
      // Create notification for receiver
      const notificationsRef = collection(db, notificationModel.collection);
      const notification = notificationModel.createNotification(
        call.receiverId,
        'video_call_incoming', // New notification type for incoming calls
        '📞 Video Call เข้ามา',
        notificationMessage,
        {
          callId: callId,
          callerId: call.callerId,
          chatId: call.chatId,
          productId: call.productId,
          storeId: call.storeId,
          action: 'join_call'
        }
      );
      
      const notificationData = {
        ...notification,
        read: false,
        readAt: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const notifDoc = await addDoc(notificationsRef, notificationData);
      console.log('✅ Incoming call notification sent to receiver:', {
        notificationId: notifDoc.id,
        receiverId: call.receiverId,
        callerId: call.callerId
      });
    } catch (notifError) {
      console.error('❌ Error sending incoming call notification:', notifError);
      // Don't throw - notification is not critical for call to work
    }
    
    return { id: callId, ...call };
  } catch (error) {
    console.error('Error creating video call:', error);
    console.error('Call data received:', callData);
    throw error;
  }
};

/**
 * Update video call (end call)
 */
export const endVideoCall = async (callId, duration) => {
  try {
    const callRef = doc(db, 'videoCalls', callId);
    await updateDoc(callRef, {
      status: 'ended',
      endedAt: serverTimestamp(),
      duration: duration,
      updatedAt: serverTimestamp()
    });
    
    // Update request status
    const callSnap = await getDoc(callRef);
    const callData = callSnap.data();
    if (callData.requestId) {
      await updateVideoCallRequest(callData.requestId, {
        status: 'completed',
        endedAt: serverTimestamp()
      });
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error ending video call:', error);
    throw error;
  }
};

// ==================== Video Call Usage ====================

/**
 * Get or create video call usage for current month
 * @param {string} identifier - storeId or userId
 * @param {boolean} isUserId - true if identifier is userId, false if storeId
 */
export const getVideoCallUsage = async (identifier, isUserId = false) => {
  try {
    const now = new Date();
    const monthStr = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`;
    const usageId = `${identifier}_${monthStr}`;
    
    const usageRef = doc(db, 'videoCallUsage', usageId);
    const usageSnap = await getDoc(usageRef);
    
    if (usageSnap.exists()) {
      return { id: usageSnap.id, ...usageSnap.data() };
    }
    
    // Create new usage record
    const newUsage = {
      [isUserId ? 'userId' : 'storeId']: identifier,
      month: monthStr,
      totalCalls: 0,
      reservedCalls: 0, // Calls that are approved but not yet started
      totalDuration: 0,
      completedCalls: 0,
      cancelledCalls: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    await setDoc(usageRef, newUsage);
    return { id: usageId, ...newUsage };
  } catch (error) {
    console.error('Error getting video call usage:', error);
    throw error;
  }
};

/**
 * Reserve video call usage (when request is approved)
 * @param {string} userId - User ID (buyer)
 * @param {string} requestId - Request ID
 */
export const reserveVideoCallUsage = async (userId, requestId) => {
  try {
    const usage = await getVideoCallUsage(userId, true);
    const usageRef = doc(db, 'videoCallUsage', usage.id);
    
    await updateDoc(usageRef, {
      reservedCalls: (usage.reservedCalls || 0) + 1,
      totalCalls: (usage.totalCalls || 0) + 1,
      updatedAt: serverTimestamp()
    });
    
    console.log('✅ Reserved video call usage for user:', userId, 'request:', requestId);
    return { success: true };
  } catch (error) {
    console.error('Error reserving video call usage:', error);
    throw error;
  }
};

/**
 * Refund video call usage (when request is rejected or cancelled after approval)
 * @param {string} userId - User ID (buyer)
 * @param {string} requestId - Request ID
 */
export const refundVideoCallUsage = async (userId, requestId) => {
  try {
    const usage = await getVideoCallUsage(userId, true);
    const usageRef = doc(db, 'videoCallUsage', usage.id);
    
    const newReservedCalls = Math.max(0, (usage.reservedCalls || 0) - 1);
    const newTotalCalls = Math.max(0, (usage.totalCalls || 0) - 1);
    
    await updateDoc(usageRef, {
      reservedCalls: newReservedCalls,
      totalCalls: newTotalCalls,
      cancelledCalls: (usage.cancelledCalls || 0) + 1,
      updatedAt: serverTimestamp()
    });
    
    console.log('✅ Refunded video call usage for user:', userId, 'request:', requestId);
    return { success: true };
  } catch (error) {
    console.error('Error refunding video call usage:', error);
    throw error;
  }
};

/**
 * Increment video call usage (when call actually starts)
 */
export const incrementVideoCallUsage = async (identifier, callData, isUserId = false) => {
  try {
    const usage = await getVideoCallUsage(identifier, isUserId);
    const usageRef = doc(db, 'videoCallUsage', usage.id);
    
    // Decrease reserved calls when call starts
    const newReservedCalls = Math.max(0, (usage.reservedCalls || 0) - 1);
    
    await updateDoc(usageRef, {
      reservedCalls: newReservedCalls,
      totalDuration: (usage.totalDuration || 0) + (callData.duration || 0),
      completedCalls: callData.status === 'ended' 
        ? (usage.completedCalls || 0) + 1 
        : (usage.completedCalls || 0),
      cancelledCalls: callData.status === 'cancelled'
        ? (usage.cancelledCalls || 0) + 1
        : (usage.cancelledCalls || 0),
      updatedAt: serverTimestamp()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error incrementing video call usage:', error);
    throw error;
  }
};

